import { ArrowRight, Pencil, Trash2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import FdeRecordStatus from '@/frontend/features/Fde/FdeRecordStatus'
import type { ActionType, FdeCopy, LinkType, OntologyInterface } from '@/frontend/features/Fde/types'

type Contract = OntologyInterface | LinkType | ActionType
interface Props { kind: 'interface' | 'link' | 'action'; items: Contract[]; copy: FdeCopy; canManage: boolean; onEdit: (record: Contract) => void; onDelete: (record: Contract) => void }

function contractMeta(record: Contract, kind: Props['kind'], copy: FdeCopy) {
  if (kind === 'interface') {
    const item = record as OntologyInterface
    return `${item.property_schema.length} ${copy.workspace.standard_properties.toLocaleLowerCase()} · ${item.class_count} ${copy.workspace.contract_classes}`
  }
  if (kind === 'link') {
    const item = record as LinkType
    return `${item.cardinality_label} · ${item.relationship_count} ${copy.workspace.contract_relationships}`
  }
  const item = record as ActionType
  return `${item.target.name} · ${item.input_schema.length} ${copy.forms.input_schema.toLocaleLowerCase()}`
}

function contractTitle(record: Contract, kind: Props['kind']) {
  if (kind !== 'link') return record.name
  const item = record as LinkType
  return <span className="flex flex-wrap items-center gap-2"><span>{item.from_class.name}</span><ArrowRight className="size-3.5 text-muted-foreground" /><strong className="text-primary">{item.forward_label}</strong><ArrowRight className="size-3.5 text-muted-foreground" /><span>{item.to_class.name}</span></span>
}

export default function OntologyContractRows({ kind, items, copy, canManage, onEdit, onDelete }: Props) {
  if (items.length === 0) {
    const message = kind === 'interface' ? copy.workspace.no_interfaces : kind === 'link' ? copy.workspace.no_link_types : copy.workspace.no_action_types
    return <div className="rounded-lg border border-dashed border-border px-5 py-8 text-center text-xs leading-5 text-muted-foreground">{message}</div>
  }

  return (
    <div className="divide-y divide-border rounded-lg border border-border bg-background">
      {items.map((record) => (
        <article key={record.id} className="group flex items-center gap-4 px-4 py-3 hover:bg-muted/15">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2 text-sm font-medium">
              {contractTitle(record, kind)}
              <Badge variant="outline">{record.source === 'baseline' ? copy.workspace.baseline_contract : copy.workspace.project_contract}</Badge>
              <FdeRecordStatus status={record.status} proposedLabel={copy.workspace.proposed} confirmedLabel={copy.workspace.confirmed_status} />
            </div>
            <p className="mt-1 truncate text-xs text-muted-foreground">{record.description || record.key}</p>
          </div>
          <span className="shrink-0 text-[11px] text-muted-foreground">{contractMeta(record, kind, copy)}</span>
          {canManage && record.source === 'project' && <div className="flex shrink-0 items-center gap-1 opacity-60 transition-opacity group-hover:opacity-100">
            <Button type="button" size="icon-sm" variant="ghost" title={copy.common.edit} onClick={() => onEdit(record)}><Pencil /></Button>
            <Button type="button" size="icon-sm" variant="ghost" title={copy.common.delete} onClick={() => onDelete(record)}><Trash2 className="text-destructive" /></Button>
          </div>}
        </article>
      ))}
    </div>
  )
}
